import { useAuth } from "@clerk/react"
import { createORPCClient } from "@orpc/client"
import { RPCLink } from "@orpc/client/fetch"
import { createORPCReactQueryUtils, type RouterUtils } from "@orpc/react-query"
import { useCallback, useMemo } from "react"
import {
	getMarketplaceOrpcUrl,
	type MarketplaceAdminOrpcClient,
} from "./client"

/**
 * Wraps an oRPC client proxy to prevent React from mistaking it for a Promise.
 * When React checks if something is a "thenable" by accessing .then, the proxy
 * would normally interpret this as a path segment and try to call /orpc/then.
 * This wrapper returns undefined for .then access to prevent that issue.
 */
function wrapClientProxy<T extends object>(client: T): T {
	return new Proxy(client, {
		get(target, prop, receiver) {
			// Return undefined for 'then' to prevent the proxy from being treated as a Promise
			if (prop === "then") {
				return undefined
			}
			const value = Reflect.get(target, prop, receiver)
			// Recursively wrap nested proxies
			if (value && typeof value === "object") {
				return wrapClientProxy(value)
			}
			return value
		},
	})
}

type GetTokenFn = () => Promise<string | null>

export interface MarketplaceOrpc {
	client: MarketplaceAdminOrpcClient
	orpc: RouterUtils<MarketplaceAdminOrpcClient>
	isAuthReady: boolean
	getToken: GetTokenFn
}

/**
 * Hook to create a marketplace-admin oRPC client with authentication.
 * Returns both the raw client, the React Query utils, and auth readiness state.
 */
export function useMarketplaceOrpc(): MarketplaceOrpc {
	const { getToken: clerkGetToken, isLoaded, isSignedIn } = useAuth()
	const isAuthReady = isLoaded && isSignedIn

	const getToken: GetTokenFn = useCallback(
		async () => clerkGetToken(),
		[clerkGetToken],
	)

	const client = useMemo(() => {
		const link = new RPCLink({
			url: getMarketplaceOrpcUrl(),
			headers: async () => {
				try {
					const token = await clerkGetToken()

					if (!token) {
						console.warn(
							"[oRPC Marketplace] No auth token available - request will be unauthorized",
						)
					}
					return token
						? {
								"Content-Type": "application/json",
								Authorization: `Bearer ${token}`,
							}
						: {
								"Content-Type": "application/json",
							}
				} catch (error) {
					console.error("[oRPC Marketplace] Error getting auth token:", error)
					return {
						"Content-Type": "application/json",
					}
				}
			},
			fetch: async (input, init) => {
				return fetch(input, {
					...init,
					credentials: "include",
				})
			},
		})

		const orpcClient = createORPCClient(link)
		return wrapClientProxy(orpcClient) as MarketplaceAdminOrpcClient
	}, [clerkGetToken])

	const orpc = useMemo(() => {
		return createORPCReactQueryUtils(client)
	}, [client])

	return { client, orpc, isAuthReady, getToken }
}

export type { MarketplaceAdminOrpcClient }
